import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { API_OPTIONS_TOKEN } from '../tokens/shared-tokens';

@Injectable({ providedIn: 'root' })
export class ApiRemoteUpdate {
  private http = inject(HttpClient);
  private apiOptions = inject(API_OPTIONS_TOKEN);

  private getFullUrl(path: string): string {
    if (!this.apiOptions?.baseUrl?.trim()) {
      throw new Error(
        'API base URL not configured. Ensure API_OPTIONS_TOKEN is provided with a valid baseUrl via provideRemoteUpdateDomain({ baseUrl: "your-api-url" }).'
      );
    }
    const baseUrl = this.apiOptions.baseUrl.trim().replace(/\/$/, ''); // Remove trailing slash
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return `${baseUrl}${cleanPath}`;
  }
  get<T>(path: string, params?: Record<string, string>): Observable<T> {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;
    return this.http.get<T>(this.getFullUrl(path), { params: httpParams });
  }

  getBlob(path: string, params?: Record<string, string>) {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;

    return this.http.get(this.getFullUrl(path), {
      params: httpParams,
      responseType: 'blob',
      observe: 'response'
    });
  }

  post<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    params?: Record<string, string>
  ): Observable<TResponse> {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;

    return this.http.post<TResponse>(this.getFullUrl(path), body ?? null, { params: httpParams });
  }
  put<TResponse, TBody>(
    path: string,
    body: TBody,
    params?: Record<string, string>
  ): Observable<TResponse> {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;
    return this.http.put<TResponse>(this.getFullUrl(path), body, { params: httpParams });
  }

  postFile<TMetadata, TResponse>(
    path: string,
    file: File,
    metadata?: TMetadata
  ): Observable<TResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    return this.http.post<TResponse>(this.getFullUrl(path), formData);
  }

  delete<T>(path: string, params?: Record<string, string>): Observable<T> {
    const httpParams = params ? new HttpParams({ fromObject: params }) : undefined;
    return this.http.delete<T>(this.getFullUrl(path), { params: httpParams });
  }
}
